import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SETTING_KEYS from '../../../constants/setting-keys';
import AuthContext from '../../../store/auth-context';
import RIGHTS_KEYS from '../../../constants/rights-keys';
import Status from '../../general-components/Status';
import styles from './ImplantInfoComponent.module.css';
import NewImplantBadge from '../new-implant-badge/NewImplantBadge';
import { toTitleCase } from '../../../utils/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowUpRightFromSquare,
    faCircleCheck as solidCheck,
    faFileExport,
} from '@fortawesome/free-solid-svg-icons';
import MRICode from '../MriCode';
import BookmarkButton from '../BookmarkButton';
import Button, { ButtonType } from '../../general-components/Button';
import { EVENT_KEYS, trackClick } from '../../../services/api/trackingService';

import { useMediaQuery } from '../../../hooks/useMediaQuery';
import FileLink from '../../files/FileLink';
import { UserFile } from '@/components/files/FilesOverviewPage';
import GoToReportingButton from '../../reports/GoToReportingButton';

export interface HistoryVersion {
    id: string;
    version: string;
    info: any;
    date?: Date;
}

export interface IImplantInfo {
    _id?: string;
    title?: string;
    model: string;
    manufacturer: string;
    type: string;
    mriStatus: number;
    mriCode?: string;
    createdAt?: Date;
    updatedAt?: Date;
    filename?: string;
    ifu?: UserFile;
    verifiedByNI: any;
    customFields?: any[];
    notes?: string;
    catalogNumbers?: any[];
}

const ImplantInfoComponent: React.FC<{
    info: IImplantInfo;
    isPreview?: boolean;
    historyVersions?: HistoryVersion[];
    selectedVersion?: HistoryVersion;
    handleChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    onPreview?: () => void;
}> = ({
    info,
    isPreview = false,
    historyVersions,
    selectedVersion,
    handleChange,
    onPreview,
}) => {
    const { t } = useTranslation();
    const ctx = useContext(AuthContext);

    const openIfu = (file: UserFile) => {
        trackClick(EVENT_KEYS.clickOpenIFU, {
            implantId: info._id,
            implantModel: info.model,
            manufacturer: info.manufacturer,
        });
        window.open(file.url, '_blank');
    };

    const oldOpenIfu = () => {
        trackClick(EVENT_KEYS.clickOpenIFU, {
            implantId: info._id,
            implantModel: info.model,
            manufacturer: info.manufacturer,
        });
        window.open(
            'https://files.nordinsight.com/source/' + info.filename,
            '_blank'
        );
    };
    const matches = useMediaQuery('(min-width: 968px)');

    return (
        <>
            {info?.title && (
                <div className={styles.info}>
                    <div className={styles.model}>
                        <h2>{info.title}</h2>
                        <Status status={info.mriStatus} />
                    </div>
                </div>
            )}
            {info.model && (
                <div className={styles.wrapper}>
                    <div className={styles.info}>
                        <div className={styles.newBadge}>
                            <NewImplantBadge createdAt={info.createdAt} />
                        </div>
                        <div className={styles.model}>
                            <h2>{info.model}</h2>
                            <div className={styles.status}>
                                <Status status={info.mriStatus} />
                            </div>
                        </div>
                        <h3>{toTitleCase(info.manufacturer)}</h3>
                        <h4>{info.type?.split(',').join(' |')}</h4>
                    </div>
                    <div className={styles.versions}>
                        {ctx.orRights([RIGHTS_KEYS.detailedInfo]) &&
                            historyVersions.length > 0 && (
                                <div className={styles.versionDropdown}>
                                    <span>
                                        {t('implantInfo.versions.lastVersion')}
                                    </span>
                                    <select
                                        className={styles.select}
                                        value={
                                            selectedVersion
                                                ? selectedVersion.id
                                                : ''
                                        }
                                        onChange={handleChange}
                                    >
                                        <option value="">
                                            {
                                                // TODO: 0th or last index for newest versiondate
                                                historyVersions[0]?.info
                                                    ?.updatedAt
                                                    ? new Date(
                                                          historyVersions[0]?.info?.updatedAt
                                                      )?.toLocaleDateString()
                                                    : new Date(
                                                          info.updatedAt
                                                      )?.toLocaleDateString()
                                            }
                                        </option>
                                        {historyVersions.map((version) => (
                                            <option
                                                key={version.id}
                                                value={version.id}
                                            >
                                                {version.version}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        {info.verifiedByNI && (
                            <div style={{ color: '#4BC17E' }}>
                                <FontAwesomeIcon icon={solidCheck} />{' '}
                                <span className="">
                                    {t('implantInfo.versions.lastChecked')}{' '}
                                    {new Date(
                                        info.verifiedByNI.date
                                    ).toLocaleDateString()}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className={styles.actions}>
                        {(ctx.orRights([RIGHTS_KEYS.viewSource]) ||
                            ctx.getContext().actor?.role === 'PremiumUser') && (
                            <>
                                {info.ifu && (
                                    <FileLink
                                        file={info.ifu}
                                        disabled={
                                            ctx.getContext().actor?.role ===
                                            'PremiumUser'
                                        }
                                        onClick={openIfu}
                                        buttonType="action"
                                        buttonText={t(
                                            'implantInfo.buttons.eIFU'
                                        )}
                                    />
                                )}
                                {info.filename && !info.ifu && (
                                    <Button
                                        disabled={
                                            ctx.getContext().actor?.role ===
                                            'PremiumUser'
                                        }
                                        type={ButtonType.action}
                                        onClick={oldOpenIfu}
                                    >
                                        <FontAwesomeIcon
                                            icon={faArrowUpRightFromSquare}
                                        />
                                        {matches
                                            ? t('implantInfo.buttons.eIFU')
                                            : ''}
                                    </Button>
                                )}
                            </>
                        )}

                        {info.mriCode &&
                            ctx.orRights([RIGHTS_KEYS.advancedSearch]) && (
                                <MRICode code={info.mriCode} />
                            )}
                        {!isPreview &&
                            ctx.orRights([RIGHTS_KEYS.advancedSearch]) && (
                                <BookmarkButton id={info._id} />
                            )}
                        {onPreview && (
                            <Button
                                type={ButtonType.action}
                                onClick={() => {
                                    trackClick(EVENT_KEYS.clickExport, { previewSource: 'implant' });
                                    onPreview();
                                }}
                            >
                                <FontAwesomeIcon icon={faFileExport} />
                                {matches
                                    ? t(
                                          'implantInfo.buttons.export',
                                          'Export'
                                      )
                                    : ''}
                            </Button>
                        )}
                        {ctx.orRights([RIGHTS_KEYS.cptReporting]) && (
                            <>
                                <GoToReportingButton implantId={info._id} />
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ImplantInfoComponent;
